import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [ "frame" ];
  static values = { backgroundId: String };

  connect() {
    this.trigger = null;
    this.initialFormState = null;
    this.savePending = false;
    this.isClosing = false;
    this.pendingSaveForm = null;
    this.closeTimer = null;
    this.closeTransitionEnd = null;
    this.closeCallback = null;
    this.background = document.getElementById(this.backgroundIdValue || "preparation-form");
    this.boundOpen = this.open.bind(this);
    this.boundFrameLoaded = this.frameLoaded.bind(this);
    this.boundSaveSubmitStart = this.saveSubmitStart.bind(this);
    this.boundSaveSubmitEnd = this.saveSubmitEnd.bind(this);
    this.frameObserver = new window.MutationObserver(() => this.captureInitialStateIfReady());

    document.addEventListener("preparation-recipe-workspace:open", this.boundOpen);
    document.addEventListener("turbo:submit-start", this.boundSaveSubmitStart);
    document.addEventListener("turbo:submit-end", this.boundSaveSubmitEnd);
    this.frameTarget.addEventListener("turbo:frame-load", this.boundFrameLoaded);
    this.frameObserver.observe(this.frameTarget, { childList: true, subtree: true });
  }

  disconnect() {
    this.cancelCloseAnimation();
    this.closeCallback = null;
    document.removeEventListener("preparation-recipe-workspace:open", this.boundOpen);
    document.removeEventListener("turbo:submit-start", this.boundSaveSubmitStart);
    document.removeEventListener("turbo:submit-end", this.boundSaveSubmitEnd);
    this.frameTarget?.removeEventListener("turbo:frame-load", this.boundFrameLoaded);
    this.frameObserver?.disconnect();
  }

  open(event) {
    const url = event.detail?.url;
    if (!url) return;

    this.cancelCloseAnimation();
    this.closeCallback = null;
    this.trigger = event.detail?.trigger || null;
    this.initialFormState = null;
    this.pendingSaveForm = null;
    this.setSavePending(false);
    this.background?.setAttribute("inert", "");

    if (this.element.showModal) {
      this.element.showModal();
    } else {
      this.element.setAttribute("open", "");
    }

    this.frameTarget.removeAttribute("src");
    this.frameTarget.innerHTML = "<p class=\"ec-field-hint p-6\">Loading recipe workspace…</p>";
    this.frameTarget.src = url;
    this.focusCloseButton();
  }

  frameLoaded() {
    if (!this.element.open) return;

    const result = this.frameTarget.querySelector("[data-recipe-workspace-result], [data-preparation-recipe-workspace-result]");
    if (result) {
      const recipeId = String(result.dataset.recipeId || "").trim();
      const recipeLabel = String(result.dataset.recipeLabel || "").trim();
      const recipeDishId = String(result.dataset.recipeDishId || "").trim();
      const recipeDishLabel = String(result.dataset.recipeDishLabel || "").trim();
      const context = String(result.dataset.recipeWorkspaceContext || "preparation_workspace").trim();
      this.closeImmediately(() => {
        if (!recipeId) return;

        document.dispatchEvent(new CustomEvent("preparation-recipe-workspace:created", {
          detail: { recipeId, recipeLabel, recipeDishId, recipeDishLabel, context }
        }));
      });
      return;
    }

    const terminal = this.frameTarget.querySelector("[data-preparation-recipe-workspace-terminal]");
    if (terminal) {
      this.pendingSaveForm = null;
      this.setSavePending(false);
      this.initialFormState = this.formState();
      terminal.setAttribute("tabindex", "-1");
      terminal.focus();
      return;
    }

    if (this.initialFormState !== null) return;

    this.initialFormState = this.formState();
    const heading = this.frameTarget.querySelector("h1, h2, h3");
    if (heading) {
      heading.setAttribute("tabindex", "-1");
      window.setTimeout(() => heading.focus(), 0);
    }
  }

  captureInitialStateIfReady() {
    if (!this.element.open || this.initialFormState !== null) return;
    if (!this.frameTarget.querySelector("form")) return;

    this.frameLoaded();
  }

  requestClose(event) {
    event?.preventDefault();
    if (this.savePending || this.isClosing) return;
    if (this.hasUnsavedChanges() && !window.confirm("Discard these unsaved recipe details and close the workspace?")) return;

    this.closeImmediately();
  }

  handleDialogCancel(event) {
    event.preventDefault();
    if (this.savePending || this.isClosing) return;
    this.requestClose(event);
  }

  saveSubmitStart(event) {
    if (!this.isWorkspaceSaveForm(event.target)) return;

    this.pendingSaveForm = event.target;
    this.setSavePending(true);
  }

  saveSubmitEnd(event) {
    if (event.target !== this.pendingSaveForm && !this.isWorkspaceSaveForm(event.target)) return;

    // Keep the workspace guarded until the successful response has rendered the
    // result state and dispatched the existing application event. A failed or
    // invalid response has no durable Recipe to protect and can release the UI.
    if (event.detail?.fetchResponse?.succeeded === true) return;

    this.pendingSaveForm = null;
    this.setSavePending(false);
  }

  isWorkspaceSaveForm(form) {
    return form instanceof window.HTMLFormElement &&
      (form.dataset.recipeWorkspaceSaveForm === "true" ||
        form.dataset.preparationRecipeWorkspaceSaveForm === "true");
  }

  setSavePending(pending) {
    this.savePending = pending;
    if (pending) this.element.setAttribute("aria-busy", "true");
    else this.element.removeAttribute("aria-busy");

    const interactionGuarded = pending || this.isClosing;
    this.frameTarget.toggleAttribute("inert", interactionGuarded);

    this.element.querySelectorAll("[data-action*='requestClose']").forEach((control) => {
      control.disabled = interactionGuarded;
      control.setAttribute("aria-disabled", String(interactionGuarded));
    });
  }

  closeImmediately(afterClose = null) {
    if (this.isClosing || this.closeTimer) return;

    this.isClosing = true;
    this.setSavePending(false);
    this.closeCallback = afterClose;

    if (this.element.open && this.element.close) {
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
        this.finishClose();
        return;
      }

      this.element.classList.add("is-closing");
      this.closeTransitionEnd = (event) => {
        if (event.target !== this.element || event.propertyName !== "transform") return;

        this.finishClose();
      };
      this.element.addEventListener("transitionend", this.closeTransitionEnd);
      this.closeTimer = window.setTimeout(() => this.finishClose(), 360);
      return;
    }

    this.finishClose();
  }

  finishClose() {
    if (!this.isClosing) return;

    const afterClose = this.closeCallback;
    this.closeCallback = null;
    this.cancelCloseAnimation();
    if (this.element.open && this.element.close) {
      this.element.close();
    } else {
      this.element.removeAttribute("open");
    }

    this.isClosing = false;
    this.setSavePending(false);
    this.background?.removeAttribute("inert");
    this.frameTarget.removeAttribute("src");
    this.frameTarget.innerHTML = "<p class=\"ec-field-hint p-6\">Loading recipe workspace…</p>";
    this.initialFormState = null;

    const trigger = this.trigger;
    this.trigger = null;
    if (trigger?.isConnected) {
      window.setTimeout(() => {
        if (trigger.isConnected) trigger.focus();
      }, 0);
    }
    afterClose?.();
  }

  cancelCloseAnimation() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (this.closeTransitionEnd) {
      this.element.removeEventListener("transitionend", this.closeTransitionEnd);
      this.closeTransitionEnd = null;
    }

    this.element.classList.remove("is-closing");
  }

  hasUnsavedChanges() {
    if (this.initialFormState === null) return false;

    return this.formState() !== this.initialFormState;
  }

  formState() {
    return Array.from(this.frameTarget.querySelectorAll("input, textarea, select"))
      .map((field) => [
        field.name,
        field.type,
        field.value,
        field.checked ? "checked" : "unchecked",
        field.disabled ? "disabled" : "enabled"
      ].join("="))
      .join("\n");
  }

  focusCloseButton() {
    window.setTimeout(() => {
      this.element.querySelector("[data-action*='requestClose']")?.focus();
    }, 0);
  }
}
